import {
    infiniteQueryOptions,
    keepPreviousData,
    queryOptions,
    skipToken,
    useMutation,
    type DefaultError,
    type UseMutationResult,
} from '@tanstack/react-query';

import { Locale, ServiceStatus } from 'chaika-enums';
import {
    ServiceCreateForm,
    ServiceData,
    ServiceListResponse,
    ServiceQuery,
    ServiceRequirementData,
    ServiceStatusUpdateForm,
    ServiceUpdateForm,
} from 'chaika-frontend-types/frontend-cms';

import { ServiceApi } from '@api/ServiceApi';
import { SpecialtyApi } from '@api/SpecialtyApi';
import { diffBindings } from '@services/utils/diffBindings';
import { queryClient } from '@store/queryClient';
import { STALE_TIME_ONE_MINUTE_MS } from '@utils/staleTime';
import { getNextOffsetPageParam } from '@utils/getNextOffsetPageParam';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';

const ACTIVE_SEARCH_LIMIT = 50;
const PAGE_SIZE = 50;

export interface UpdateServiceParams {
    id: string;
    params: ServiceUpdateForm;
}

export interface UpdateServiceStatusParams {
    id: string;
    params: ServiceStatusUpdateForm;
}

export interface UpdateServiceTagsParams {
    id: string;
    tagIds: string[];
    currentTagIds: string[];
}

export interface UpdateServiceSpecialtiesParams {
    id: string;
    specialtyIds: string[];
    currentSpecialtyIds: string[];
}

export interface UpdateServiceRequirementsParams {
    id: string;
    requirements: ServiceRequirementData[];
}

export const serviceKeys = {
    all: ['service'] as const,
    lists: () => [...serviceKeys.all, 'list'] as const,
    list: (params: ServiceQuery, locale: Locale | undefined) =>
        [...serviceKeys.lists(), 'byParams', params, locale] as const,
    infiniteList: (filters: ServiceQuery, locale: Locale | undefined) =>
        [...serviceKeys.lists(), 'infinite', { filters }, locale] as const,
    byIds: (ids: string[], locale: Locale | undefined) =>
        [...serviceKeys.lists(), 'byIds', getUniqSortedIds(ids), locale] as const,
    byIdsWithTranslations: (ids: string[]) =>
        [...serviceKeys.lists(), 'byIdsWithTranslations', getUniqSortedIds(ids)] as const,
    activeSearch: (query: string | undefined, locale: Locale | undefined) =>
        [...serviceKeys.lists(), 'activeSearch', query?.trim() || '', locale] as const,
};

export const serviceQueryOptions = {
    list: (params: ServiceQuery, locale: Locale | undefined) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: serviceKeys.list(params, locale),
            queryFn: async (): Promise<ServiceData[]> => {
                const { services } = await ServiceApi.getList({ ...params, locale });
                return services;
            },
        }),
    infiniteList: (filters: ServiceQuery, locale: Locale | undefined) =>
        infiniteQueryOptions({
            staleTime: STALE_TIME_ONE_MINUTE_MS,
            queryKey: serviceKeys.infiniteList(filters, locale),
            queryFn: ({ pageParam }): Promise<ServiceListResponse> =>
                ServiceApi.getList({ ...filters, locale, limit: PAGE_SIZE, offset: pageParam }),
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => getNextOffsetPageParam(lastPage, allPages, page => page.services),
            placeholderData: keepPreviousData,
        }),
    byIds: (ids: string[], locale: Locale | undefined) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: serviceKeys.byIds(ids, locale),
            queryFn:
                ids.length > 0
                    ? (): Promise<ServiceData[]> =>
                          loadWithChunkAndFlatten<ServiceData>(ids, async chunk => {
                              const { services } = await ServiceApi.getList({ ids: chunk.ids, locale });

                              return services;
                          })
                    : skipToken,
        }),
    byIdsWithTranslations: (ids: string[]) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: serviceKeys.byIdsWithTranslations(ids),
            queryFn:
                ids.length > 0
                    ? (): Promise<ServiceData[]> =>
                          loadWithChunkAndFlatten<ServiceData>(ids, async chunk => {
                              const { services } = await ServiceApi.getList({
                                  ids: chunk.ids,
                                  withTranslations: true,
                              });

                              return services;
                          })
                    : skipToken,
        }),
    activeSearch: (query: string | undefined, locale: Locale | undefined) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: serviceKeys.activeSearch(query, locale),
            queryFn: async (): Promise<ServiceData[]> => {
                const { services } = await ServiceApi.getList({
                    statuses: ServiceStatus.Active,
                    query,
                    locale,
                    limit: ACTIVE_SEARCH_LIMIT,
                });
                return services;
            },
        }),
};

export function useCreateService(): UseMutationResult<void, DefaultError, ServiceCreateForm> {
    return useMutation({
        mutationFn: (params: ServiceCreateForm) => ServiceApi.create(params),
    });
}

export function useUpdateService(): UseMutationResult<void, DefaultError, UpdateServiceParams> {
    return useMutation({
        mutationFn: ({ id, params }: UpdateServiceParams) => ServiceApi.update(id, params),
    });
}

export function useUpdateServiceStatus(): UseMutationResult<void, DefaultError, UpdateServiceStatusParams> {
    return useMutation({
        mutationFn: ({ id, params }: UpdateServiceStatusParams) => ServiceApi.updateStatus(id, params),
        onSuccess: invalidateServiceLists,
    });
}

// Теги и специальности ниже — шаги сохранения услуги: список инвалидирует хендлер после всех шагов,
// иначе один Save даёт три рефетча.
export function useUpdateServiceTags(): UseMutationResult<void, DefaultError, UpdateServiceTagsParams> {
    return useMutation({
        mutationFn: async ({ id, tagIds, currentTagIds }: UpdateServiceTagsParams) => {
            const { idsToBind, idsToUnbind } = diffBindings(currentTagIds, tagIds);

            for (const tagId of idsToUnbind) {
                await ServiceApi.unbindTag(id, tagId);
            }

            for (const tagId of idsToBind) {
                await ServiceApi.bindTag(id, { tagId });
            }
        },
    });
}

export function useUpdateServiceSpecialties(): UseMutationResult<void, DefaultError, UpdateServiceSpecialtiesParams> {
    return useMutation({
        mutationFn: async ({ id, specialtyIds, currentSpecialtyIds }: UpdateServiceSpecialtiesParams) => {
            const { idsToBind, idsToUnbind } = diffBindings(currentSpecialtyIds, specialtyIds);

            for (const specialtyId of idsToUnbind) {
                await SpecialtyApi.unbindService(specialtyId, id);
            }

            for (const specialtyId of idsToBind) {
                await SpecialtyApi.bindService(specialtyId, { serviceId: id });
            }
        },
    });
}

export function useUpdateServiceRequirements(): UseMutationResult<void, DefaultError, UpdateServiceRequirementsParams> {
    return useMutation({
        mutationFn: ({ id, requirements }: UpdateServiceRequirementsParams) =>
            ServiceApi.updateRequirements(id, requirements),
        onSuccess: invalidateServiceLists,
    });
}

export function invalidateServiceLists(): void {
    queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
}
